import {
  Button,
  chakra,
  HStack,
  ListIcon,
  ListItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import { FormProvider, useForm } from "react-hook-form";
import DatepickerFormInput from "../TicketTypes/DatepickerFormInput";
import { MdCheckCircle } from "react-icons/md";
import { FaRegCircle } from "react-icons/fa";
import { FiXCircle } from "react-icons/fi";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const InlineText = chakra.span;

const DuplicateEventModal = ({
  data,
  onClose,
  isLoading = false,
  isOpen = false,
  onSave,
}: {
  onClose: () => void;
  isOpen: boolean;
  data: {
    name?: string;
    timezone?: string;
    start_at?: string;
  };
  onSave: (props: { start_at: string }) => Promise<void>;
  isLoading: boolean;
}) => {
  const methods = useForm({
    mode: "all",
    criteriaMode: "all",
    reValidateMode: "onBlur",
    values: {
      start_at: data.start_at,
    },
  });
  const { handleSubmit } = methods;

  return (
    <Modal
      size={"3xl"}
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Duplicate event</ModalHeader>
        <ModalBody>
          <VStack w="full">
            <Text w="full">
              Duplicating event will create a clone of the current event with
              the name{" "}
              <InlineText fontWeight={"bold"}>{data.name} Copy</InlineText> in a
              draft state.
            </Text>
            <Text w="full">The following details will be copied over:</Text>
            <HStack alignItems={"flex-start"} width={"full"}>
              <UnorderedList w="full" listStyleType={"none"}>
                <ListItem>
                  <ListIcon as={MdCheckCircle} color="green.500" />
                  Event name, image, venue and description
                </ListItem>
                <ListItem>
                  <ListIcon as={MdCheckCircle} color="green.500" />
                  Event timelines
                </ListItem>
                <ListItem>
                  <ListIcon as={MdCheckCircle} color="green.500" />
                  Lineup
                </ListItem>
                <ListItem>
                  <ListIcon as={MdCheckCircle} color="green.500" />
                  Genres
                </ListItem>
                <ListItem>
                  <ListIcon as={MdCheckCircle} color="green.500" />
                  Ticket types
                </ListItem>
                <ListItem>
                  <ListIcon as={MdCheckCircle} color="green.500" />
                  Marketing pixels
                </ListItem>
                <ListItem>
                  <ListIcon as={MdCheckCircle} color="green.500" />
                  Affiliates
                </ListItem>
              </UnorderedList>
              <UnorderedList w="full" listStyleType="none">
                <ListItem>
                  <ListIcon as={FiXCircle} color="red.500" />
                  Access codes
                </ListItem>
                <ListItem>
                  <ListIcon as={FiXCircle} color="red.500" />
                  Promo codes
                </ListItem>
                <ListItem>
                  <ListIcon as={FiXCircle} color="red.500" />
                  Guestlist
                </ListItem>
                <ListItem>
                  <ListIcon as={FiXCircle} color="red.500" />
                  Ticket report recipients
                </ListItem>
                <ListItem>
                  <ListIcon as={FiXCircle} color="red.500" />
                  Shared organizations
                </ListItem>
                <ListItem>
                  <ListIcon as={FiXCircle} color="red.500" />
                  Tickets and sales
                </ListItem>
              </UnorderedList>
            </HStack>
            <Text w="full">
              No changes will be made to the current event. You'll be redirected
              to the duplicated event.
            </Text>
            <VStack mt={4} w="full">
              <Text fontSize={"small"}>
                <InlineText mr={2} fontWeight={"bold"}>
                  Attention:
                </InlineText>
                The date you insert below will set the start date of the new
                event. All the other event and ticket type dates will be
                adjusted accordingly (by keeping the same time difference from
                the event start date).
              </Text>
              <FormProvider {...methods}>
                <DatepickerFormInput
                  label="Start date of the new event"
                  name="start_at"
                  timezone={data.timezone}
                  validate={(val) => {
                    return dayjs(val).isAfter(dayjs());
                  }}
                  validationFailMessage="Start date must be in the future"
                />
              </FormProvider>
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            isDisabled={isLoading}
            onClick={() => {
              onClose();
            }}
            mr="4"
          >
            Cancel
          </Button>
          <Button
            color="white"
            bg="green.500"
            isDisabled={!methods.formState.isValid}
            isLoading={isLoading}
            onClick={handleSubmit(onSave)}
          >
            Duplicate
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DuplicateEventModal;
